import { Controller, useFormContext } from 'react-hook-form'

import { DatePicker } from '@wartek-id/date-picker'
import { Select } from '@wartek-id/select'
import { Text } from '@wartek-id/text-v1'
import { Textarea } from '@wartek-id/textarea'
import { Input } from '@wartek-id/input'

import type { IPelaksanaanDocumentPreparationForm } from 'types/kinerja-pelaksanaan'
import type { SchoolItem } from 'types/kinerja-gpk'

const MIN_YEAR = 2025

interface JadwalObservasiFormProps {
  disabled: boolean
  schools: SchoolItem[]
  isLoadingSchoolList: boolean

  selectedSchool?: {
    npsn: string
    schoolName: string
  }
}

export const JadwalObservasiForm = ({
  disabled,
  schools,
  isLoadingSchoolList,
  selectedSchool,
}: JadwalObservasiFormProps) => {
  const { control, register, formState } =
    useFormContext<IPelaksanaanDocumentPreparationForm>()
  const formErrors = formState.errors

  return (
    <div className="p-6 space-y-4">
      <Text>
        Jadwal dan lokasi dapat berupa perkiraan saja dan dapat diubah di luar
        sistem. Langsung diskusikan dengan Tim Kinerja atau Pejabat Penilai
        Kinerja, ya.
      </Text>

      {/* Tanggal */}
      <div className="w-72 max-w-full space-y-1">
        <Text variant="action" as="label" htmlFor="tanggal-observasi">
          Tanggal
        </Text>
        <Controller
          control={control}
          name="observationDate"
          rules={{
            required: {
              value: true,
              message: 'Wajib dipilih',
            },
          }}
          render={({ field, fieldState }) => {
            const errorMessage = fieldState?.error?.message
            const isInvalid = !!errorMessage

            return (
              <>
                <DatePicker
                  aria-describedby="tanggal-observasi-error"
                  useCalendar
                  value={field.value}
                  minYear={MIN_YEAR}
                  minValue={`${MIN_YEAR}-01-01`}
                  isDisabled={disabled}
                  onChange={field.onChange}
                  isInvalid={isInvalid}
                  errorMessage={errorMessage}
                />
              </>
            )
          }}
        />
      </div>

      {/* Lokasi Observasi */}
      <div className="max-w-full space-y-1">
        <div className="flex flex-col gap-1">
          <Text variant="action" as="label" htmlFor="lokasi-observasi">
            Lokasi Observasi
          </Text>
          <Text color="subdued">
            Disarankan pilih satuan pendidikan yang paling membutuhkan dukungan
            dan dapat Anda dampingi secara langsung. Tim Kinerja akan
            mengobservasi Anda di lokasi ini.
          </Text>
        </div>
        <div className="w-8/12 max-w-full">
          {selectedSchool && disabled ? (
            <Input value={selectedSchool.schoolName} type="text" isDisabled />
          ) : (
            <Controller
              name="observationLocation"
              control={control}
              rules={{
                validate: (value) => {
                  if (!value?.npsn) {
                    return 'Wajib dipilih'
                  }
                  return true
                },
              }}
              render={({ field, fieldState }) => {
                const errorMessage = fieldState?.error?.message
                const isInvalid = !!errorMessage

                return (
                  <div className="space-y-1">
                    <Select
                      id="lokasi-observasi"
                      placeholder="Pilih di sini"
                      name={field.name}
                      value={field.value.npsn}
                      isDisabled={disabled || isLoadingSchoolList}
                      onChange={(e) => field.onChange({ npsn: e.target.value })}
                      aria-invalid={isInvalid}
                      aria-describedby="lokasi-observasi-error"
                      isInvalid={isInvalid}
                      errorMessage={errorMessage}
                    >
                      <option value="" disabled>
                        Pilih di sini
                      </option>

                      {schools.map((school) => (
                        <option key={school.npsn} value={school.npsn}>
                          {school.schoolName}
                        </option>
                      ))}
                    </Select>
                  </div>
                )
              }}
            />
          )}
        </div>
      </div>

      {/* Perangkat Pendukung */}
      <div className="w-8/12 max-w-full space-y-1 relative">
        <Text variant="action" as="label" htmlFor="perangkat-pendukung">
          Perangkat Pendukung
        </Text>
        <Textarea
          isDisabled={disabled}
          maxLength={250}
          rows={3}
          placeholder="Contoh: Hasil pengumpulan data dari Formulir Umpan Balik dari orang tua untuk didiskusikan, dsb."
          id="perangkat-pendukung"
          {...register('observationTool', {
            maxLength: 250,
            required: {
              value: true,
              message: 'Wajib diisi',
            },
          })}
          isInvalid={!!formErrors.observationTool}
          errorMessage={formErrors.observationTool?.message}
        />
      </div>

      {/* Catatan lain */}
      <div className="w-8/12 max-w-full space-y-1 relative">
        <Text variant="action" as="label" htmlFor="catatan">
          Catatan lain (Opsional)
        </Text>
        <Textarea
          maxLength={250}
          rows={3}
          id="catatan"
          isDisabled={disabled}
          placeholder="Contoh: Jam observasi. Anda juga dapat menuliskan catatan lain jika ada."
          {...register('observationNote', { maxLength: 250 })}
        />
      </div>
    </div>
  )
}
